import { type ImageToken } from '@1debit/chime-assets';
import { type MouseEvent, useEffect } from 'react';

import { Button } from '../../server/Button/Button';
import { CircledIconButton } from '../../server/CircledIconButton/CircledIconButton';
import { RemoteImage } from '../../server/RemoteImage/RemoteImage';
import { classNames } from '../../server/utils/classNames';
import { type BannerAnalyticsData, useBannerShownAnalytics } from '../AnalyticsProvider/hooks/useBannerShownAnalytics';
import { type CtaProps } from '../StatusBanner/StatusBanner';

type BaseProps = {
	analyticsData: BannerAnalyticsData;
	title: string;
	image?: ImageToken;
	/** If passed in, renders a clickable CTA */
	ctaProps?: CtaProps;
	className?: string;
	/** If passed in, renders a close button */
	onDismiss?: (event: MouseEvent<HTMLButtonElement>) => void;
};

export type PromoBannerProps = BaseProps & {
	variant: 'standard' | 'eyebrow';
};

const commonContainerStyle = 'max-w-[800px] h-full flex gap-2 overflow-hidden rounded-lg bg-accent-green-100';

const StandardPromoBanner = ({ analyticsData, title, image, ctaProps, className, onDismiss }: BaseProps) => {
	const { unique_id: analyticsId } = analyticsData ?? {};

	const containerStyle = classNames(commonContainerStyle, 'items-start py-2 px-4', onDismiss && 'pr-2', className);

	return (
		<div className={containerStyle}>
			<div className={classNames('flex gap-2 items-center flex-1 my-2')}>
				<div className="flex flex-col gap-2 items-start flex-1">
					<p className="text-label">{title}</p>
					{ctaProps?.label && (
						<Button
							variant="actionLink"
							className="text-label-sm text-content-action-primary"
							analyticsId={`${analyticsId}_cta_click`}
							onClick={ctaProps.onClick}>
							{ctaProps.label}
						</Button>
					)}
				</div>
				{image && <RemoteImage token={image} alt="Promo banner image" width="48" height="48" />}
			</div>
			{onDismiss && (
				<CircledIconButton
					analyticsId={`${analyticsId}_dismiss_banner`}
					icon="micro_close"
					ariaLabel="Close"
					onClick={onDismiss}
				/>
			)}
		</div>
	);
};

const EyebrowPromoBannerContent = ({ analyticsData, title, image, ctaProps, className, onDismiss }: BaseProps) => {
	const { unique_id: analyticsId } = analyticsData ?? {};

	const containerStyle = classNames(commonContainerStyle, 'items-center pl-4', onDismiss ? 'pr-2' : 'pr-4', className);

	return (
		<div className={containerStyle}>
			<div className="py-2 flex gap-2 items-center flex-1">
				{image && <RemoteImage token={image} alt="Promo banner image" width="48" height="48" />}
				<div className="flex flex-col items-center flex-1">
					<p className="text-label">{title}</p>
					{ctaProps?.label && (
						<Button
							variant="link"
							className="self-start text-sm"
							analyticsId={`${analyticsId}_cta_click`}
							onClick={ctaProps.onClick}>
							{ctaProps.label}
						</Button>
					)}
				</div>
			</div>
			{onDismiss && (
				<CircledIconButton
					analyticsId={`${analyticsId}_dismiss_banner`}
					icon="micro_close"
					ariaLabel="Close"
					onClick={onDismiss}
				/>
			)}
		</div>
	);
};

export const PromoBanner = ({
	analyticsData,
	title,
	image,
	onDismiss,
	ctaProps,
	className,
	variant,
}: PromoBannerProps) => {
	const { handleBannerShown } = useBannerShownAnalytics(analyticsData);

	// send analytics on mount
	useEffect(() => handleBannerShown(), [handleBannerShown]);

	const commonProps = { analyticsData, title, image, ctaProps, className, onDismiss };

	return variant === 'standard' ? (
		<StandardPromoBanner {...commonProps} />
	) : (
		<EyebrowPromoBannerContent {...commonProps} />
	);
};
